<script setup>
import TextItem from '@/containers/popup/components/TextItemV2.vue';
import TextItems from '@/containers/popup/components/TextItems.vue';
import TagBox from '@/containers/popup/components/TagBoxV3.vue';
import i18n from '@/i18n';
import { computed, getCurrentInstance, ref } from 'vue';
import { useProductTypeGroups } from '@/composables/useProductTypeGroups.js';
import { getCustomLabels } from '@/containers/utils/utils.js';

const { getProductTypeGroups } = useProductTypeGroups();
const BLOCK_STORAGE = ['VM', 'Bare Metal Server', 'VM-GPU', 'MULTINODE-GPU'];
const DATA_OPS_FLOW = ['DataOps', 'DataFlow'];
const LB = 'LB';
const PRODUCT_TYPE_CODE = 'productTypeCode';

// define props component
const props = defineProps({
  targetData: {
    type: Object,
    default: () => ({})
  },
  detailFilter: {
    type: Object,
    default: () => ({})
  },
  productTypeCode: {
    type: String,
    default: ''
  },
  chipItems: {
    type: Array,
    default: () => []
  },
  customFields: {
    type: Object,
    default: () => ({})
  }
});

// use global and plugin in vue instance
const { $http, $util } = getCurrentInstance().appContext.config.globalProperties;
const { t } = i18n.global;

// ref, reactive declare
const detailObject = ref(null);
const iconProductType = ref(null);

// Computed
const isInternetGateway = computed(() => props.productTypeCode === 'IGW');
const isBlockStorage = computed(() => BLOCK_STORAGE.includes(props.productTypeCode));
const isDataOpsDataFlow = computed(() => DATA_OPS_FLOW.includes(props.productTypeCode));

const customFields = {
  productName: {
    attrs: {
      textLineClamp: true
    }
  },
  productTypeCode: {
    attrs: {
      icon: iconProductType.value,
      widthIcon: '20'
    },
    customFieldValue: (target) => target.productTypeName
  },
  productState: {
    customFieldValue: (target) => $util.runningIcon(target.productState, target.productState),
    attrs: {
      isHtml: true
    }
  },
  clusterName: {
    customFieldValue: () =>
      isDataOpsDataFlow.value
        ? props.customFields.dataOpsClusterName
        : props.customFields.productClusterName
  },
  clusSizeCpu: {
    customShow: () => props.targetData.clusSizeCpu || props.targetData.clusSizeMem,
    customFieldValue: () => `${props.targetData.clusSizeCpu} ${props.targetData.clusSizeMem || ''}`
  },
  diskSize: {
    customShow: () => !isBlockStorage.value,
    customFieldValue: () => props.customFields.productDisk
  },
  storageInfo: {
    attrs: {
      blockStorage: true
    },
    customShow: () => isBlockStorage.value,
    customFieldValue: () => props.customFields.productStorageInfo
  },
  protocol: {
    customFieldValue: (target) => props.customFields.productProtocol || target.protocol
  },
  serviceDomain: {
    customFieldValue: () => props.customFields.serviceDomain
  },
  dstTgwName: {
    customFieldValue: () => props.customFields.dstTgwName
  },
  connectedVPC: {
    customShow: () => isInternetGateway.value,
    customFieldValue: () => props.customFields.vpcId
  },
  isIGW: {
    customFieldValue: () => (props.customFields.vpcIsIGW === 'Y' ? 'Peering + Public' : 'Peering')
  },
  hpcNodeCount: {
    customFieldValue: () => props.customFields.hpcNodeCount
  },
  podCount: {
    customFieldValue: () => props.customFields.podCount
  },
  lbName: {
    customShow: () => props.productTypeCode !== LB
  },
  logging: {
    attrs: {
      beforeTooltip: true
    }
  }
};

// Function of components
const customAttrs = (attrs, key) => {
  if (!attrs) return attrs;
  if (key === PRODUCT_TYPE_CODE && Object.hasOwn(attrs, 'icon')) {
    attrs.icon = iconProductType.value;
  }
  return attrs;
};

const initMonTargetInfo = (detailList) => {
  if (!detailList || !detailList.length) return;

  const customLabels = getCustomLabels(props.productTypeCode);

  detailObject.value = detailList.reduce((acc, { apifieldname, fieldname }) => {
    if (apifieldname) {
      const formatedLabel = customLabels[apifieldname]
        ? t(`searchLabel.${customLabels[apifieldname]}`)
        : fieldname;

      acc[apifieldname] = {
        fieldName: formatedLabel,
        customFieldValue: customFields[apifieldname]?.customFieldValue,
        attrs: customAttrs(customFields[apifieldname]?.attrs, apifieldname) ?? {}
      };
    }

    return acc;
  }, {});
};
const isConnectedVPCNotIGW = (key) => key === 'connectedVPC' && !isInternetGateway.value;
const isProductTags = (key) => key === 'productTags';

const handleValueShow = (detail, key) => {
  if (detail.customFieldValue) return detail.customFieldValue(props.targetData);
  return props.targetData[key];
};

const checkFieldShow = (detail, key) => {
  if (!detail.customShow) return Object.hasOwn(detailObject.value, key);
  return Object.hasOwn(detailObject.value, key) && detail.customShow(props.targetData);
};
const getGroup = (listGroup, productTypeCd) => {
  return listGroup.find((item) => {
    const { productTypeCode, childItems } = item;
    if (productTypeCode && productTypeCode.toLowerCase() === productTypeCd.toLowerCase())
      return true;
    if (!childItems) return false;
    return getGroup(item.childItems, productTypeCd);
  });
};
const getIconProductType = async () => {
  try {
    const data = await $http.product.getProductTypeList();
    const productTypeGroups = await getProductTypeGroups(data);
    const group = getGroup(productTypeGroups, props.productTypeCode);
    if (group) {
      iconProductType.value = group.icon;
    }
    return true;
  } catch (e) {
    return false;
  }
};
const init = async () => {
  const result = await getIconProductType();
  if (result) initMonTargetInfo(props.detailFilter);
};
// Created Component hook
init();
</script>

<template>
  <div class="monitoring-info-wrap">
    <div class="monitoring-info-section monitoring-target-section">
      <div v-for="(detail, key) in detailObject" :key="key">
        <TagBox v-if="isProductTags(key)" :label="detail.fieldName" :tagItems="chipItems" />
        <TextItems
          v-else-if="isConnectedVPCNotIGW(key)"
          :label="detail.fieldName"
          :values="targetData.connectedVpcItems"
          withoutPointer
        />
        <TextItem
          v-else-if="checkFieldShow(detail, key)"
          :label="detail.fieldName"
          :value="handleValueShow(detail, key)"
          v-bind="detail.attrs"
          withoutPointer
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.monitoring-info-wrap {
  box-sizing: border-box;

  .monitoring-inner-wrap {
    :deep .simplebar-track.simplebar-vertical {
      right: 6px;
    }

    :deep .simplebar-content {
      height: 148px;
    }
  }
}

.monitoring-info-section {
  display: flex;
  flex-direction: column;
  gap: 36px;
  width: 100%;

  .left-col {
    width: 50%;
  }

  .right-col {
    width: 50%;
  }
}

.monitoring-target-section {
  padding: 0 20px 0 0px;
}
</style>
